import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Shell from "../components/Shell";
import { api, eventTypes, eventTypeLabel, daysUntil } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, CalendarDays, MapPin, Repeat, Sparkles, Eye, X } from "lucide-react";
import EventActionsMenu from "../components/EventActionsMenu";
import { tabsForEvent, familyLabel } from "../lib/eventPresets";

import OverviewTab from "../components/tabs/OverviewTab";
import BudgetTab from "../components/tabs/BudgetTab";
import VendorsTab from "../components/tabs/VendorsTab";
import TasksTab from "../components/tabs/TasksTab";
import TimelineTab from "../components/tabs/TimelineTab";
import LiveTab from "../components/tabs/LiveTab";
import SetupStrikeTab from "../components/tabs/SetupStrikeTab";
import GuestsTab from "../components/tabs/GuestsTab";
import SeatingTab from "../components/tabs/SeatingTab";
import RsvpRegistryTab from "../components/tabs/RsvpRegistryTab";
import DocumentsTab from "../components/tabs/DocumentsTab";
import ChatTab from "../components/tabs/ChatTab";
import RehearsalTab from "../components/tabs/RehearsalTab";
import { summarizeRecurrence } from "../components/RecurrenceControl";

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ev, setEv] = useState(null);
  const [members, setMembers] = useState([]);
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  // "View as vendor" preview — planner-side simulation of what a given member
  // will see once they sign in. Non-destructive, client-side only.
  const [previewAs, setPreviewAs] = useState(null);

  const generateNextOccurrences = async () => {
    if (!ev?.is_recurring) return;
    setGenerating(true);
    try {
      const { data } = await api.post(`/events/${id}/materialize-occurrences`, { count: 12 });
      if (data.created > 0) {
        toast.success(`Generated ${data.created} upcoming occurrence${data.created === 1 ? "" : "s"}`);
      } else {
        toast.info("No new occurrences to generate (all upcoming dates already exist or the series has ended)");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate occurrences");
    } finally {
      setGenerating(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: e }, { data: m }] = await Promise.all([
        api.get(`/events/${id}`),
        api.get(`/events/${id}/members`),
      ]);
      setEv(e);
      setMembers(m);
    } catch (err) {
      console.error("EventDetail load failed:", err);
      toast.error("Could not load event");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading || !ev) {
    return <Shell><div className="p-10 text-muted">Loading event…</div></Shell>;
  }

  const days = daysUntil(ev.event_date);
  const typeLabel = eventTypeLabel(ev.event_type);
  const trueIsOwner = ev.my_role === "owner" || ev.my_role === "coordinator";
  // When previewing as a vendor, drop all edit capability so the planner sees
  // exactly the read-only surface that vendor would land on after sign-in.
  const isOwner = trueIsOwner && !previewAs;
  const canPostUpdate = previewAs
    ? previewAs.role !== "client"
    : ev.my_role !== "client";
  // Tabs a vendor / coordinator / client shouldn't be dropped into on their
  // first visit. When previewing, hide the ones the target role wouldn't see.
  const hiddenTabsForPreview = (() => {
    if (!previewAs) return new Set();
    if (previewAs.role === "vendor") return new Set(["budget", "vendors", "guests", "seating", "rsvp"]);
    if (previewAs.role === "client") return new Set(["budget", "vendors", "guests", "seating"]);
    return new Set(); // coordinator — sees everything, just read-only
  })();
  // Only pulse the recording dot when the event is happening today — the rest of the
  // year the tab stays a calm static red so it doesn't scream during planning weeks.
  const isEventToday = (() => {
    if (!ev.event_date) return false;
    const d = new Date(ev.event_date);
    const today = new Date();
    return (
      d.getFullYear() === today.getFullYear() &&
      d.getMonth() === today.getMonth() &&
      d.getDate() === today.getDate()
    );
  })();

  return (
    <Shell>
      <div>
        {/* Preview-mode banner — persistent, sticky, unmistakable */}
        {previewAs && (
          <div
            data-testid="preview-mode-banner"
            className="sticky top-0 z-20 bg-[#F59E0B] text-white border-b border-[#78350F]"
          >
            <div className="px-6 md:px-10 py-2.5 flex items-center gap-4 flex-wrap">
              <Eye size={14} strokeWidth={2} className="shrink-0" />
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase shrink-0">
                PREVIEW / VIEWING AS
              </span>
              <span className="font-semibold text-sm truncate">{previewAs.name}</span>
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase opacity-80 shrink-0">
                · {previewAs.role.toUpperCase()} · READ-ONLY
              </span>
              <button
                type="button"
                onClick={() => setPreviewAs(null)}
                data-testid="preview-mode-exit"
                className="ml-auto flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase px-2.5 py-1 border border-white/40 hover:bg-surface hover:text-[#F59E0B] transition-colors"
              >
                <X size={12} strokeWidth={2} /> Exit preview
              </button>
            </div>
          </div>
        )}

        {/* Technical header — command bar style */}
        <div className="border-b border-hair bg-surface">
          {/* Top strip: back link + sysid */}
          <div className="px-6 md:px-10 py-3 flex items-center justify-between border-b border-hair">
            <Link to="/dashboard" data-testid="back-to-dashboard" className="flex items-center gap-1.5 font-mono text-[10px] tracking-widest uppercase text-muted hover:text-app">
              <ArrowLeft size={12} strokeWidth={2} /> All events
            </Link>
            <div className="flex items-center gap-3">
              <div className="font-mono text-[10px] tracking-widest uppercase text-muted">
                EVT.{ev.id.slice(-8).toUpperCase()}
              </div>
              {(ev.my_role === "owner" || ev.my_role === "planner" || ev.my_role === "coordinator") && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await api.post(`/events/${ev.id}/start`);
                      navigate(`/events/${ev.id}/command`);
                    } catch (e) {
                      navigate(`/events/${ev.id}/command`);
                    }
                  }}
                  data-testid="start-event-btn"
                  title="Enter Command Mode"
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bevel-btn-primary text-white font-medium text-xs uppercase tracking-widest"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-white/90 live-pulse" aria-hidden />
                  Start Event
                </button>
              )}
              <EventActionsMenu
                ev={{ id: ev.id, title: ev.title, role: ev.my_role, archived_at: ev.archived_at }}
                variant="button"
                afterDelete="/events"
                onChanged={load}
              />
            </div>
          </div>

          {/* Main info row */}
          <div className="px-6 md:px-10 py-6 flex items-start justify-between gap-6 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-2 flex items-center gap-2 flex-wrap">
                <span
                  className="px-1.5 py-0.5 rounded-sm bg-inset border border-hair text-app"
                  data-testid="event-family-badge"
                >
                  {familyLabel(ev)}
                </span>
                <span className="text-faint">/</span>
                <span>{typeLabel}</span>
                {ev.client && (
                  <>
                    <span className="text-faint">/</span>
                    <Link to={`/clients/${ev.client.id}`} data-testid="event-client-link" className="text-app hover:underline">
                      {ev.client.name}
                    </Link>
                  </>
                )}
              </div>
              <h1 className="font-display text-3xl md:text-5xl font-black text-app leading-none tracking-tight" data-testid="event-title">
                {ev.title}
              </h1>
              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
                <div className="flex items-center gap-1.5 text-app">
                  <CalendarDays size={13} strokeWidth={1.75} className="text-muted" />
                  <span className="font-mono uppercase tracking-wider">{new Date(ev.event_date).toLocaleDateString(undefined, { weekday:"short", year:"numeric", month:"short", day:"2-digit"}).toUpperCase()}</span>
                </div>
                {ev.venue && (
                  <div className="flex items-center gap-1.5 text-app">
                    <MapPin size={13} strokeWidth={1.75} className="text-muted" />
                    <span>{ev.venue}</span>
                  </div>
                )}
                {ev.is_recurring && (
                  <div className="flex items-center gap-1.5 text-[#F59E0B]" data-testid="event-recurrence-badge">
                    <Repeat size={13} strokeWidth={1.75} />
                    <span className="font-mono uppercase tracking-wider">{summarizeRecurrence(ev) || "Recurring"}</span>
                    <button
                      type="button"
                      onClick={generateNextOccurrences}
                      disabled={generating}
                      data-testid="generate-next-occurrences-btn"
                      className="ml-1 inline-flex items-center gap-1 border border-hair hover:border-[#0A0A0A] disabled:opacity-50 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest text-app transition-colors"
                    >
                      <Sparkles size={10} strokeWidth={2} />
                      {generating ? "Generating…" : "Gen 12"}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Countdown card */}
            {days !== null && (
              <div className="border border-hair bg-inset px-5 py-3 min-w-[160px]" style={{ borderRadius: "2px" }}>
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-1 flex items-center gap-1.5">
                  T-MINUS
                  {isEventToday && <span className="w-1.5 h-1.5 rounded-full bg-[#EF4444] live-pulse" />}
                </div>
                <div className={`font-mono font-semibold tabular-nums leading-none ${days === 0 ? "text-[#EF4444] text-3xl" : days > 0 ? "text-app text-3xl" : "text-faint text-2xl"}`}>
                  {days === 0 ? "TODAY" : days > 0 ? `${days}D` : `+${Math.abs(days)}D`}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={hiddenTabsForPreview.has(tab) ? "overview" : tab} onValueChange={setTab}>
          <div className="border-b border-hair bg-surface sticky top-0 z-10">
            <TabsList className="tabs-underline h-auto bg-transparent p-0 px-6 md:px-10 gap-6 rounded-none justify-start overflow-x-auto w-full">
              {tabsForEvent(ev)
                .filter((t) => !hiddenTabsForPreview.has(t.key))
                .map(({ key: v, label: l, live: isLive }) => (
                <TabsTrigger
                  key={v}
                  value={v}
                  data-testid={`tab-${v}`}
                  className={
                    isLive
                      ? "rounded-none border-b-2 border-transparent data-[state=active]:border-red-600 data-[state=active]:text-red-600 data-[state=active]:shadow-none text-red-600 hover:text-red-700 px-0 py-4 text-sm font-semibold tracking-wide inline-flex items-center gap-2 ml-auto uppercase"
                      : "rounded-none border-b-2 border-transparent data-[state=active]:border-[#0A0A0A] data-[state=active]:text-app data-[state=active]:shadow-none text-muted hover:text-app px-0 py-4 text-sm font-medium tracking-wide"
                  }
                >
                  {isLive && (
                    <span
                      aria-hidden
                      className="relative inline-flex items-center justify-center"
                      data-testid={isEventToday ? "live-tab-dot-pulse" : "live-tab-dot-static"}
                    >
                      {isEventToday && (
                        <span className="absolute inline-flex h-2.5 w-2.5 rounded-full bg-red-500 opacity-75 animate-ping" />
                      )}
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600" />
                    </span>
                  )}
                  {l}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <div className="p-6 md:p-10 max-w-6xl">
            <TabsContent value="overview"><OverviewTab ev={ev} members={members} canEdit={isOwner} reload={load} /></TabsContent>
            <TabsContent value="budget"><BudgetTab eventId={ev.id} members={members} canEdit={isOwner} /></TabsContent>
            <TabsContent value="vendors"><VendorsTab eventId={ev.id} members={members} canEdit={isOwner} reload={load} onPreviewAs={trueIsOwner ? setPreviewAs : null} /></TabsContent>
            <TabsContent value="tasks"><TasksTab eventId={ev.id} members={members} canEdit={isOwner} /></TabsContent>
            <TabsContent value="timeline"><TimelineTab eventId={ev.id} members={members} canEdit={isOwner} /></TabsContent>
            <TabsContent value="live"><LiveTab eventId={ev.id} members={members} canEdit={isOwner} canPost={canPostUpdate} myRole={previewAs ? previewAs.role : ev.my_role} /></TabsContent>
            <TabsContent value="rehearsal"><RehearsalTab ev={ev} canEdit={isOwner} reload={load} /></TabsContent>
            <TabsContent value="guests"><GuestsTab eventId={ev.id} canEdit={isOwner} /></TabsContent>
            <TabsContent value="seating"><SeatingTab eventId={ev.id} ev={ev} canEdit={isOwner} reload={load} /></TabsContent>
            <TabsContent value="rsvp"><RsvpRegistryTab ev={ev} canEdit={isOwner} reload={load} /></TabsContent>
            <TabsContent value="documents"><DocumentsTab eventId={ev.id} members={members} canEdit={isOwner} previewAs={previewAs} /></TabsContent>
            <TabsContent value="chat" className="p-0 -mx-6 md:-mx-10 -mt-6 md:-mt-10"><ChatTab eventId={ev.id} members={members} canEdit={isOwner} myRole={previewAs ? previewAs.role : ev.my_role} /></TabsContent>
            <TabsContent value="setup"><SetupStrikeTab eventId={ev.id} members={members} canEdit={isOwner} /></TabsContent>
          </div>
        </Tabs>
      </div>
    </Shell>
  );
}
